import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import SplashScreen from "./SplashScreen";
import {
  Search,
  ChevronDown,
  ChevronRight,
  Menu,
  X,
  Globe2,
  User,
  Bookmark,
  Settings,
  LogOut,
  Sparkles,
  CreditCard,
  Smartphone,
} from "lucide-react";
import { AboutModal, LegalModal, SupportModal } from "./FooterModals";
import { useAuth } from "./AuthContext";
import { CONTINENTS } from "./continents";
import { getTicker, getCategories } from "../../utils/api";
import { SiteBreadcrumbs } from "./navigation/SiteBreadcrumbs";
import { DnhSearchLink } from "./search/SearchNavigation";
export { SiteFooter } from "./CompactFooter";

/* ============================================================
   DNH — Site Chrome (shared across pages)
   Header · LiveTicker · Footer (with About/Legal/Support modals)
   ============================================================ */

export const LOGO_URL = "/dnh-logo.svg";

/* ── PWA "Add to Home Screen" hook ────────────────────────────────────
   Captures the native beforeinstallprompt (Chrome/Edge/Android). iOS
   Safari never fires this event, so we fall back to on-screen
   instructions there instead of a native prompt. */
const useInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(
    () => window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true
  );

  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => { setInstalled(true); setDeferredPrompt(null); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const isIos = /iphone|ipad|ipod/i.test(window.navigator.userAgent);

  const promptInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") setInstalled(true);
      setDeferredPrompt(null);
      return "native";
    }
    return isIos ? "ios" : "unsupported";
  };

  return { promptInstall, canInstall: !!deferredPrompt || isIos, installed };
};

/* ── Footer "Get the Web App" badge ──────────────────────────────────── */
const InstallAppBadge = () => {
  const { promptInstall, canInstall, installed } = useInstallPrompt();
  const [showIosHint, setShowIosHint] = useState(false);

  if (installed) return null;

  const handleClick = async () => {
    const result = await promptInstall();
    if (result === "ios") setShowIosHint(true);
  };

  return (
    <div className="relative mt-2.5" data-testid="footer-install-app-wrapper">
      <button
        type="button"
        onClick={handleClick}
        className="dnh-chip inline-flex items-center gap-2 h-9 px-3 rounded-full border border-[var(--crimson)]/35 bg-[var(--crimson)]/[0.05] font-sans text-[12px] text-[var(--crimson)] hover:bg-[var(--crimson)]/[0.09] transition-colors"
        data-testid="footer-install-app-badge"
      >
        <Smartphone size={14} strokeWidth={1.75} />
        <span className="flex flex-col items-start leading-[1.15]">
          <span className="font-semibold">Get the Web App</span>
          <span className="font-mono text-[9px] tracking-[0.06em] uppercase text-[var(--crimson)]/75">
            Add to home screen
          </span>
        </span>
      </button>

      {showIosHint && (
        <div
          className="absolute left-0 bottom-full mb-2 w-64 rounded-[10px] border border-[var(--hairline)] bg-[var(--paper)] shadow-lg p-3.5 z-20"
          data-testid="footer-install-ios-hint"
        >
          <p className="font-sans text-[12px] text-[var(--ink-2)] leading-[1.5]">
            On iPhone/iPad: tap the <strong>Share</strong> icon in Safari, then
            choose <strong>&ldquo;Add to Home Screen.&rdquo;</strong>
          </p>
          <button
            type="button"
            onClick={() => setShowIosHint(false)}
            className="mt-2 font-mono text-[9.5px] tracking-[0.08em] uppercase text-[var(--muted)] hover:text-[var(--ink)]"
            data-testid="footer-install-ios-hint-close"
          >
            Got it
          </button>
        </div>
      )}
    </div>
  );
};

/* Row 2 editorial navigation — order matches approved two-row layout */
export const EDITORIAL_NAV = [
  { label: "Home",    to: "/",        testId: "nav-home" },
  { label: "Today",   to: "/today",   testId: "nav-today" },
  { label: "Explore", to: "/explore", testId: "nav-explore" },
  { label: "For You", to: "/for-you", testId: "nav-for-you" },
];

/* Legacy aliases — kept for any imports elsewhere */
export const LOGGED_OUT_NAV = EDITORIAL_NAV;
export const LOGGED_IN_NAV  = EDITORIAL_NAV;

/* Hover-underline nav link — shared by desktop & active-state logic */
const NavItem = ({ item, activeNav }) => {
  const isActive = item.label === activeNav;
  return (
    <Link
      to={item.to}
      data-testid={item.testId}
      className={`relative group py-1 text-[14px] font-medium whitespace-nowrap transition-colors duration-150 ${
        isActive ? "text-[var(--ink)]" : "text-[var(--ink-2)] hover:text-[var(--ink)]"
      }`}
    >
      {item.label}
      {/* Slide-in underline */}
      <span
        aria-hidden
        className={`absolute bottom-[-3px] left-0 right-0 h-[1.5px] bg-[var(--crimson)] origin-left transition-transform duration-200 ease-out ${
          isActive ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
        }`}
      />
    </Link>
  );
};


/* ──────────────────────────────────────────────────────────────
   Explore dropdown — Geographic + Categories + Topics
   ────────────────────────────────────────────────────────────── */
const EXPLORE_CONTINENT_TO_REGION_PARAM = {
  AS: "Asia", AF: "Africa", EU: "Europe", NA: "Americas", SA: "Americas", OC: "Oceania",
};

const EXPLORE_CATEGORIES_FALLBACK = [
  { label: "Technology",    slug: "technology",    signals: 0 },
  { label: "Politics",      slug: "politics",      signals: 0 },
  { label: "Business",      slug: "business-economy", signals: 0 },
  { label: "Health",        slug: "health-lifestyle",  signals: 0 },
  { label: "Science",       slug: "science",       signals: 0 },
  { label: "Sports",        slug: "sports",        signals: 0 },
  { label: "Climate",       slug: "climate-energy", signals: 0 },
  { label: "Entertainment", slug: "entertainment", signals: 0 },
];

const fmtSignals = (n) => {
  if (!n) return null;
  if (n >= 10000) return `${Math.round(n / 1000)}k`;
  if (n >= 1000)  return `${(n / 1000).toFixed(1).replace(/\.0$/, "")}k`;
  return `${n}`;
};

const ExploreNavDropdown = ({ activeNav }) => {
  const [open, setOpen] = useState(false);
  const [categories, setCategories] = useState(EXPLORE_CATEGORIES_FALLBACK);
  const wrapRef = useRef(null);
  const isActive =
    activeNav === "Explore" ||
    activeNav === "Categories" ||
    activeNav === "Topics" ||
    activeNav === "Countries";

  /* Load live categories once on mount */
  useEffect(() => {
    getCategories()
      .then((d) => {
        const cats = d?.categories || [];
        if (cats.length) setCategories(cats.map((c) => ({ label: c.name, slug: c.slug, signals: c.signals_today || 0 })));
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* Close on outside click */
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const continents = Object.values(CONTINENTS).filter((c) => c.id !== "AN");

  return (
    <div className="relative" ref={wrapRef} data-testid="nav-explore-wrap">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="nav-explore-trigger"
        className={`relative group inline-flex items-center gap-1 py-1 text-[14px] font-medium whitespace-nowrap transition-colors duration-150 ${
          isActive || open ? "text-[var(--ink)]" : "text-[var(--ink-2)] hover:text-[var(--ink)]"
        }`}
      >
        Explore
        <ChevronDown size={13} strokeWidth={2} className={`transition-transform ${open ? "rotate-180" : ""}`} />
        <span
          aria-hidden
          className={`absolute bottom-[-3px] left-0 right-0 h-[1.5px] bg-[var(--crimson)] origin-left transition-transform duration-200 ease-out ${
            isActive ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
          }`}
        />
      </button>

      {open && (
        <div
          role="menu"
          data-testid="nav-explore-panel"
          className="absolute left-1/2 -translate-x-1/2 mt-3 w-[420px] bg-[var(--paper)] border border-[var(--hairline-strong)] rounded-[12px] overflow-hidden shadow-[0_24px_60px_-8px_rgba(17,17,17,0.18)] z-[80]"
        >
          {/* ── Geographic ── */}
          <div className="px-4 py-2.5 border-b border-[var(--hairline)] bg-[var(--surface-2)]">
            <div className="font-mono text-[9.5px] tracking-[0.18em] uppercase text-[var(--muted)]">
              Geographic
            </div>
          </div>
          <div className="py-1.5 grid grid-cols-3 gap-0.5 px-1.5">
            {continents.map((c) => (
              <Link
                key={c.id}
                to={`/countries?region=${EXPLORE_CONTINENT_TO_REGION_PARAM[c.id]}`}
                onClick={() => setOpen(false)}
                data-testid={`nav-explore-continent-${c.id.toLowerCase()}`}
                className="flex items-center gap-2 px-3 py-2 rounded-[8px] text-[12.5px] text-[var(--ink-2)] hover:bg-[var(--surface-2)] hover:text-[var(--ink)] transition-colors"
              >
                <Globe2 size={12} strokeWidth={1.75} className="text-[var(--muted)] shrink-0" />
                {c.label}
              </Link>
            ))}
          </div>
          <div className="border-b border-[var(--hairline)] pb-1.5 px-1.5">
            <Link
              to="/countries"
              onClick={() => setOpen(false)}
              data-testid="nav-explore-all-countries"
              className="flex items-center justify-between px-3 py-1.5 rounded-[8px] text-[13px] font-medium text-[var(--ink)] hover:bg-[var(--surface-2)] transition-colors"
            >
              Browse all 196 countries
              <ChevronRight size={13} strokeWidth={2} className="text-[var(--muted)]" />
            </Link>
          </div>

          {/* ── Categories ── */}
          <div className="px-4 py-2.5 border-b border-[var(--hairline)] bg-[var(--surface-2)]">
            <div className="font-mono text-[9.5px] tracking-[0.18em] uppercase text-[var(--muted)]">
              Categories
            </div>
          </div>
          <div className="py-1.5 grid grid-cols-4 gap-0.5 px-1.5">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                onClick={() => setOpen(false)}
                data-testid={`nav-explore-category-${cat.slug}`}
                className="flex flex-col items-center justify-center px-2 py-2.5 rounded-[8px] text-[var(--ink-2)] hover:bg-[var(--surface-2)] hover:text-[var(--ink)] transition-colors text-center gap-0.5 group"
              >
                <span className="text-[12px] leading-tight">{cat.label}</span>
                {fmtSignals(cat.signals) && (
                  <span className="font-mono text-[9px] text-[var(--muted)] group-hover:text-[var(--ink-2)] transition-colors">
                    {fmtSignals(cat.signals)}
                  </span>
                )}
              </Link>
            ))}
          </div>

          {/* ── Topics ── */}
          <div className="border-t border-[var(--hairline)] py-1.5 px-1.5">
            <Link
              to="/topics"
              onClick={() => setOpen(false)}
              data-testid="nav-explore-topics"
              className="flex items-center justify-between px-3 py-1.5 rounded-[8px] text-[13px] font-medium text-[var(--ink)] hover:bg-[var(--surface-2)] transition-colors"
            >
              Browse all Topics
              <ChevronRight size={13} strokeWidth={2} className="text-[var(--muted)]" />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};


const UserMenu = ({ user }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const navigate = useNavigate();
  const { logout } = useAuth();
  const isPremium =
    user?.subscription_tier === "premium" ||
    user?.subscription_tier === "super_admin" ||
    user?.is_admin;

  // Close on outside click + Escape
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const handleLogout = async () => {
    setOpen(false);
    try {
      if (!await logout()) return;
    } catch (_) { return; }
    navigate("/");
  };

  const items = [
    { label: "Your account", to: "/account", icon: User, testId: "user-menu-account" },
    { label: "For You", to: "/for-you", icon: Bookmark, testId: "user-menu-for-you" },
    isPremium
      ? { label: "Manage subscription", to: "/account?tab=settings", icon: CreditCard, testId: "user-menu-billing" }
      : { label: "Upgrade — $12/year", to: "/subscribe", icon: Sparkles, testId: "user-menu-upgrade", accent: true },
    { label: "Settings", to: "/account?tab=settings", icon: Settings, testId: "user-menu-settings" },
    ...(user?.is_admin ? [{ label: "Admin Dashboard", to: "/admin", icon: Settings, testId: "user-menu-admin", admin: true }] : []),
  ];

  return (
    <div className="relative ml-2" ref={wrapRef} data-testid="user-menu-wrap">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`${user.name} — open menu`}
        title={user.name}
        data-testid="header-user-avatar"
        className="h-9 w-9 rounded-full bg-[var(--ink)] text-[var(--paper)] flex items-center justify-center font-mono text-[11px] tracking-[0.06em] hover:bg-[var(--crimson)] transition-colors dnh-focus overflow-hidden"
      >
        {user.avatar_url ? (
          <img src={user.avatar_url} alt={user.name} className="h-full w-full object-cover" />
        ) : (
          user.initials
        )}
      </button>

      {open && (
        <div
          role="menu"
          aria-label="User menu"
          data-testid="user-menu-dropdown"
          className="absolute right-0 mt-2 w-[260px] bg-[var(--paper)] border border-[var(--hairline-strong)] rounded-[10px] overflow-hidden shadow-[0_24px_60px_-8px_rgba(17,17,17,0.18)] z-[80]"
        >
          {/* Identity header */}
          <div className="px-4 py-3 border-b border-[var(--hairline)] bg-[var(--surface-2)]">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-full bg-[var(--ink)] text-[var(--paper)] flex items-center justify-center font-mono text-[11px] overflow-hidden shrink-0">
                {user.avatar_url ? (
                  <img src={user.avatar_url} alt={user.name} className="h-full w-full object-cover" />
                ) : (
                  user.initials
                )}
              </div>
              <div className="min-w-0">
                <div className="font-sans text-[13.5px] font-medium text-[var(--ink)] truncate">
                  {user.name}
                </div>
                <div className="font-mono text-[10px] text-[var(--muted)] truncate">
                  {user.email}
                </div>
              </div>
            </div>
            <div className="mt-2.5 flex items-center gap-1.5">
              <span
                className={`inline-flex items-center font-mono text-[9px] tracking-[0.18em] uppercase px-2 py-[3px] rounded-full ${
                  isPremium
                    ? "text-[var(--paper)] bg-[var(--crimson)]"
                    : "text-[var(--ink-2)] border border-[var(--hairline)]"
                }`}
              >
                {isPremium ? "Premium" : "Free"}
              </span>
              {isPremium && user.subscription_expires_at && (
                <span className="font-mono text-[9.5px] text-[var(--muted)]">
                  Renews{" "}
                  {new Date(user.subscription_expires_at).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })}
                </span>
              )}
            </div>
          </div>

          {/* Menu items */}
          <div className="py-1.5">
            {items.map((it) => (
              <Link
                key={it.label}
                to={it.to}
                onClick={() => setOpen(false)}
                data-testid={it.testId}
                role="menuitem"
                className={`flex items-center gap-2.5 px-4 py-2 text-[13.5px] hover:bg-[var(--surface-2)] transition-colors ${
                  it.admin ? "text-[var(--crimson)] font-medium border-t border-[var(--hairline)] mt-1 pt-3" :
                  it.accent ? "text-[var(--crimson)] font-medium" : "text-[var(--ink)]"
                }`}
              >
                <it.icon size={14} strokeWidth={1.75} className={it.admin || it.accent ? "text-[var(--crimson)]" : "text-[var(--ink-2)]"} />
                {it.label}
                {it.admin && (
                  <span className="ml-auto font-mono text-[9px] tracking-[0.16em] uppercase border border-[var(--crimson)]/40 text-[var(--crimson)] rounded-full px-1.5 py-[2px]">
                    Admin
                  </span>
                )}
              </Link>
            ))}
          </div>

          {/* Logout */}
          <div className="border-t border-[var(--hairline)] py-1.5">
            <button
              type="button"
              onClick={handleLogout}
              data-testid="user-menu-logout"
              role="menuitem"
              className="w-full text-left flex items-center gap-2.5 px-4 py-2 text-[13.5px] text-[var(--ink)] hover:bg-[var(--surface-2)] transition-colors"
            >
              <LogOut size={14} strokeWidth={1.75} className="text-[var(--ink-2)]" />
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export const FORMATTED_DATE = new Date().toLocaleDateString("en-GB", {
  weekday: "long",
  day: "numeric",
  month: "long",
  year: "numeric",
});

export const TICKER_ITEMS = [
  "Geneva · US–China trade talks open",
  "Frankfurt · ECB holds rates",
  "Abu Dhabi · Joint AI framework advances",
  "Bengaluru · Semiconductor package cleared",
  "Tokyo · BoJ signals policy shift",
  "Brussels · EU AI Act deadline guidance",
  "Sao Paulo · Selic cut 50bps",
  "London · FCA listing reform delay",
  "Tel Aviv · Ceasefire enters third week",
  "Bengaluru · ISRO Gaganyaan window confirmed",
];

/* ----------------------- Offline Banner ----------------------- */
const OfflineBanner = () => {
  const [offline, setOffline] = useState(!navigator.onLine);
  useEffect(() => {
    const goOffline = () => setOffline(true);
    const goOnline  = () => setOffline(false);
    window.addEventListener("offline", goOffline);
    window.addEventListener("online",  goOnline);
    return () => {
      window.removeEventListener("offline", goOffline);
      window.removeEventListener("online",  goOnline);
    };
  }, []);
  if (!offline) return null;
  return (
    <div
      data-testid="offline-banner"
      className="fixed bottom-0 left-0 right-0 z-[9998] flex items-center justify-center gap-2 px-4 py-2.5 text-[12px] font-medium text-white"
      style={{
        background: "rgba(30,30,30,0.96)",
        backdropFilter: "blur(8px)",
        paddingBottom: "calc(env(safe-area-inset-bottom) + 10px)",
      }}
    >
      <span className="inline-block w-2 h-2 rounded-full bg-amber-400 shrink-0" />
      You're offline — showing cached content
    </div>
  );
};

/* ----------------------- Header ----------------------- */
export const SiteHeader = ({ activeNav = "Home", user: userProp, ticker = false }) => {
  const [open, setOpen] = useState(false);
  const headerRef = useRef(null);
  const { user: authUser } = useAuth();
  const location = useLocation();
  useLayoutEffect(() => {
    const header = headerRef.current;
    const measure = () => document.documentElement.style.setProperty("--site-header-height", `${header.getBoundingClientRect().height}px`);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(header);
    return () => { observer.disconnect(); document.documentElement.style.removeProperty("--site-header-height"); };
  }, []);

  // Prefer explicit prop (Admin page) over auth context
  const rawUser = userProp || authUser;

  // Normalise to a consistent shape regardless of source
  const user = rawUser
    ? {
        ...rawUser,
        initials: (rawUser.initials || (rawUser.name
          ? rawUser.name.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2)
          : "U")),
        plan: rawUser.plan || rawUser.subscription_tier || "free",
      }
    : null;

  const showSubscribeCta = !user || user.plan === "free";

  return (<>
    <SplashScreen />
    <OfflineBanner />
    <header
      ref={headerRef}
      className="sticky top-0 z-50 bg-[var(--paper)]"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
      data-testid="site-header"
    >
      {/* ── ROW 1: Brand + Utility ── */}
      <div className="border-b border-[var(--hairline)]" data-testid="site-header-brand-row">
        <div className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-10 h-14 flex items-center gap-3">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-3 dnh-focus shrink-0"
            data-testid="site-header-logo-link"
            aria-label="Daily News Highlights home"
          >
            <img
              src={LOGO_URL}
              alt="Daily News Highlights"
              className="h-9 lg:h-10 w-auto"
              data-testid="site-header-logo"
            />
          </Link>

          {/* Date — desktop only */}
          <span className="hidden lg:block font-mono text-[10px] tracking-[0.14em] uppercase text-[var(--muted)] whitespace-nowrap ml-2 select-none">
            {FORMATTED_DATE}
          </span>

          <div className="flex-1" />

          {/* Desktop utility actions */}
          <div className="hidden xl:flex items-center gap-1">
            <DnhSearchLink
              className="relative group inline-flex items-center gap-2 h-9 px-3 text-[13px] font-medium text-[var(--ink-2)] hover:text-[var(--ink)] dnh-focus transition-colors whitespace-nowrap"
              data-testid="header-search-button"
              aria-label="DNH Search"
              aria-current={location.pathname === "/search" ? "page" : undefined}
              title="DNH Search"
            >
              <Search size={15} strokeWidth={1.75} />
              <span>Search</span>
              <kbd className="hidden 2xl:inline-flex items-center h-[17px] px-1.5 bg-[var(--surface-2)] border border-[var(--hairline)] rounded text-[9px] font-mono text-[var(--muted)] ml-0.5 select-none">
                ⌘K
              </kbd>
              <span
                aria-hidden
                className="absolute left-3 right-3 bottom-1.5 h-[1.5px] bg-[var(--crimson)] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-200 ease-out"
              />
            </DnhSearchLink>

            <span className="h-5 w-px bg-[var(--hairline)] mx-1.5" />

            {user ? (
              <>
                {showSubscribeCta && (
                  <Link
                    to="/subscribe"
                    className="relative group h-9 px-4 text-[13px] font-medium bg-[var(--ink)] text-[var(--paper)] hover:bg-[var(--crimson)] transition-colors rounded-full inline-flex items-center overflow-hidden whitespace-nowrap"
                    data-testid="site-header-upgrade"
                  >
                    <span className="relative z-10">Subscribe</span>
                  </Link>
                )}
                <UserMenu user={user} />
              </>
            ) : (
              <>
                <Link
                  to={`/signin?from=${encodeURIComponent(location.pathname + location.search)}`}
                  className="relative group h-9 px-3 text-[13px] font-medium text-[var(--ink)] inline-flex items-center transition-colors hover:text-[var(--crimson)] whitespace-nowrap"
                  data-testid="site-header-sign-in"
                >
                  Sign in
                  <span
                    aria-hidden
                    className="absolute left-3 right-3 bottom-1.5 h-[1.5px] bg-[var(--crimson)] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-200 ease-out"
                  />
                </Link>
                <Link
                  to="/subscribe"
                  className="h-9 px-4 text-[13px] font-medium bg-[var(--ink)] text-[var(--paper)] hover:bg-[var(--crimson)] transition-colors rounded-full inline-flex items-center whitespace-nowrap"
                  data-testid="site-header-subscribe"
                >
                  Subscribe
                </Link>
              </>
            )}
          </div>

          {/* ─── Tablet: Sign in + Subscribe visible alongside hamburger ─── */}
          <div className="hidden md:flex xl:hidden items-center gap-2">
            {user ? (
              <>
                {showSubscribeCta && (
                  <Link
                    to="/subscribe"
                    className="h-8 px-3 text-[12px] font-medium bg-[var(--ink)] text-[var(--paper)] hover:bg-[var(--crimson)] transition-colors rounded-full inline-flex items-center whitespace-nowrap"
                    data-testid="tablet-header-upgrade"
                  >
                    Subscribe
                  </Link>
                )}
                <UserMenu user={user} />
              </>
            ) : (
              <>
                <Link
                  to={`/signin?from=${encodeURIComponent(location.pathname + location.search)}`}
                  className="h-8 px-3 text-[12px] font-medium text-[var(--ink)] inline-flex items-center hover:text-[var(--crimson)] transition-colors whitespace-nowrap"
                  data-testid="tablet-header-sign-in"
                >
                  Sign in
                </Link>
                <Link
                  to="/subscribe"
                  className="h-8 px-3 text-[12px] font-medium bg-[var(--ink)] text-[var(--paper)] hover:bg-[var(--crimson)] transition-colors rounded-full inline-flex items-center whitespace-nowrap"
                  data-testid="tablet-header-subscribe"
                >
                  Subscribe
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu toggle — mobile only (hidden at md+) */}
          <button
            className="md:hidden inline-flex items-center justify-center h-10 w-10 -mr-2 dnh-focus"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
            aria-expanded={open}
            aria-controls={open ? "site-mobile-menu" : undefined}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* ── ROW 2: Original editorial palette, current four destinations ── */}
      <div className="border-b border-[var(--hairline)] bg-[var(--surface-2)]" data-testid="site-header-nav-row">
        <nav
          className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-10 h-10 flex items-center gap-6"
          data-testid="site-header-primary-nav"
        >
          {EDITORIAL_NAV.map((item) => (
            <NavItem key={item.label} item={item} activeNav={activeNav} />
          ))}
        </nav>
      </div>

      {/* ── Mobile menu drawer — mobile only (hidden at md+) ── */}
      {open && (
        <div
          className="md:hidden border-t border-[var(--hairline)] bg-[var(--paper)]"
          id="site-mobile-menu"
          data-testid="mobile-menu-drawer"
        >
          <div className="px-4 py-4 flex flex-col gap-1">
            {/* Editorial nav items */}
            {EDITORIAL_NAV.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                className="h-11 flex items-center justify-between text-[15px] font-medium text-[var(--ink)] hover:text-[var(--crimson)] border-b border-[var(--hairline)]"
                onClick={() => setOpen(false)}
                data-testid={`mobile-${item.testId}`}
              >
                <span>{item.label}</span>
                <ChevronRight size={16} className="text-[var(--muted)]" />
              </Link>
            ))}

            {/* Explore section */}
            <div className="pt-2 pb-1">
              <div className="font-mono text-[9px] tracking-[0.18em] uppercase text-[var(--muted)] py-1.5 px-1">
                Explore
              </div>
              <Link
                to="/countries"
                className="h-10 flex items-center justify-between text-[14px] font-medium text-[var(--ink-2)] hover:text-[var(--crimson)] border-b border-[var(--hairline)] pl-3"
                onClick={() => setOpen(false)}
                data-testid="mobile-nav-countries"
              >
                <span>Countries & Regions</span>
                <ChevronRight size={15} className="text-[var(--muted)]" />
              </Link>
              <Link
                to="/explore?view=categories"
                className="h-10 flex items-center justify-between text-[14px] font-medium text-[var(--ink-2)] hover:text-[var(--crimson)] border-b border-[var(--hairline)] pl-3"
                onClick={() => setOpen(false)}
                data-testid="mobile-nav-categories"
              >
                <span>Categories</span>
                <ChevronRight size={15} className="text-[var(--muted)]" />
              </Link>
              <Link
                to="/topics"
                className="h-10 flex items-center justify-between text-[14px] font-medium text-[var(--ink-2)] hover:text-[var(--crimson)] border-b border-[var(--hairline)] pl-3"
                onClick={() => setOpen(false)}
                data-testid="mobile-nav-topics"
              >
                <span>Topics</span>
                <ChevronRight size={15} className="text-[var(--muted)]" />
              </Link>
            </div>

            {/* Search */}
            <DnhSearchLink
              onClick={() => setOpen(false)}
              className="h-11 flex items-center justify-between text-[15px] font-medium text-[var(--ink)] hover:text-[var(--crimson)] border-b border-[var(--hairline)]"
              data-testid="mobile-nav-search"
              aria-label="DNH Search"
              aria-current={location.pathname === "/search" ? "page" : undefined}
            >
              <span className="inline-flex items-center gap-2"><Search size={15} strokeWidth={1.75} /> Search</span>
              <ChevronRight size={16} className="text-[var(--muted)]" />
            </DnhSearchLink>

            {/* Auth buttons */}
            <div className="flex gap-2 mt-3">
              {user ? (
                <>
                  {showSubscribeCta && (
                    <Link
                      to="/subscribe"
                      onClick={() => setOpen(false)}
                      className="flex-1 h-11 text-[14px] font-medium bg-[var(--ink)] text-[var(--paper)] hover:bg-[var(--crimson)] transition-colors rounded-full inline-flex items-center justify-center"
                      data-testid="mobile-site-header-upgrade"
                    >
                      Subscribe
                    </Link>
                  )}
                  <Link
                    to="/account"
                    onClick={() => setOpen(false)}
                    className="flex-1 h-11 text-[14px] font-medium text-[var(--ink)] border border-[var(--hairline)] rounded-full inline-flex items-center justify-center hover:text-[var(--crimson)] hover:border-[var(--crimson)] transition-colors"
                    data-testid="mobile-header-account"
                  >
                    {user.name.split(" ")[0]}
                  </Link>
                </>
              ) : (
                <>
                  <Link
                    to={`/signin?from=${encodeURIComponent(location.pathname + location.search)}`}
                    onClick={() => setOpen(false)}
                    className="flex-1 h-11 text-[14px] font-medium text-[var(--ink)] border border-[var(--hairline)] rounded-full inline-flex items-center justify-center hover:text-[var(--crimson)] hover:border-[var(--crimson)] transition-colors"
                    data-testid="mobile-site-header-sign-in"
                  >
                    Sign in
                  </Link>
                  <Link
                    to="/subscribe"
                    onClick={() => setOpen(false)}
                    className="flex-1 h-11 text-[14px] font-medium bg-[var(--ink)] text-[var(--paper)] hover:bg-[var(--crimson)] transition-colors rounded-full inline-flex items-center justify-center"
                    data-testid="mobile-site-header-subscribe"
                  >
                    Subscribe
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
    {ticker && <TickerStrip {...(typeof ticker === "object" ? ticker : {})} />}
    <SiteBreadcrumbs />
    </>
  );
};

/* ----------------------- Ticker strip ----------------------- */
export const TickerStrip = ({
  items: overrideItems,
  label = "Live signals",
  updated = "",
}) => {
  const [liveItems, setLiveItems] = useState([]);
  useEffect(() => {
    if (overrideItems !== undefined) return;
    let cancelled = false;
    getTicker().then((d) => { if (!cancelled) setLiveItems(d.signals || []); }).catch(() => {});
    return () => { cancelled = true; };
  }, [overrideItems]);
  const sourceItems = overrideItems !== undefined ? overrideItems : liveItems;
  const items = [...sourceItems, ...sourceItems]; // duplicate for seamless loop
  return (
    <div
      className="dnh-ticker-strip sticky top-[var(--site-header-height)] z-40 w-full min-w-0 max-w-full border-b border-[#1e1e1e] bg-[var(--ink)] overflow-hidden"
      data-testid="ticker-strip"
    >
      <div className="mx-auto w-full min-w-0 max-w-[1280px] px-4 sm:px-6 lg:px-10 h-7 flex items-center gap-6">
        <div className="hidden sm:flex items-center gap-2 shrink-0">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-[var(--crimson)] animate-pulse" />
          <span className="font-mono text-[10.5px] tracking-[0.16em] uppercase text-[#aaaaaa]">
            {label}
          </span>
        </div>
        <div className="dnh-ticker-wrapper relative flex-1 min-w-0 overflow-hidden">
          <div className="dnh-ticker-track whitespace-nowrap font-mono text-[11.5px] text-[#888888]">
            {items.map((t, i) => (
              <span key={i} className="inline-flex items-center gap-2">
                <span className="text-[#e0e0e0]">{t}</span>
                <span className="text-[#3a3a3a]">|</span>
              </span>
            ))}
          </div>
        </div>
        <div className="hidden md:block shrink-0 font-mono text-[10.5px] text-[#666666]">
          {updated}
        </div>
      </div>
    </div>
  );
};

/* ----------------------- Footer ----------------------- */

/**
 * Smooth-scroll to an in-page section, accounting for the sticky header.
 * Falls back to a no-op gracefully if the target isn't on the current page.
 */
const scrollToSelector = (selector) => (e) => {
  if (!selector) return;
  const el =
    typeof document !== "undefined" ? document.querySelector(selector) : null;
  if (!el) return;
  e.preventDefault();
  const HEADER_OFFSET = 100; // two-row header (56 + 40) + ticker
  const top = el.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
  window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
  if (el.id) {
    window.history.replaceState(null, "", `#${el.id}`);
  }
};

const LegacySiteFooter = ({
  variant = "global",
  countryName,
  categoryName,
  topicName,
  sectionsOverride,
  geoOverride,
  categoriesOverride,
} = {}) => {
  const [aboutOpen, setAboutOpen] = useState(false);
  const [legalOpen, setLegalOpen] = useState(false);
  const [legalTab, setLegalTab] = useState("terms");
  const [supportOpen, setSupportOpen] = useState(false);

  const openLegal = (tab) => {
    setLegalTab(tab);
    setLegalOpen(true);
  };

  // Default (global / home) sections
  const defaultSections = [
    { label: "Today's Global Brief", target: '[data-testid="todays-global-brief"]' },
    { label: "Live Intelligence", target: '[data-testid="live-intelligence-section"]' },
    { label: "Live Intelligence Map", target: '[data-testid="world-pulse-section"]' },
    { label: "Featured Country", target: '[data-testid="featured-country-section"]' },
    { label: "Trending Topics", target: '[data-testid="trending-topics-section"]' },
    { label: "Intelligence Categories", target: '[data-testid="categories-section"]' },
    { label: "Methodology", target: '[data-testid="why-trust-section"]' },
    { label: "Newsletter", target: '[data-testid="newsletter-section"]' },
  ];

  // Default global "Countries" column
  const defaultGeo = {
    h: "Countries",
    items: [
      { name: "India", slug: "india" },
      { name: "United States", slug: "united-states" },
      { name: "United Kingdom", slug: "united-kingdom" },
      { name: "Brazil", slug: "brazil" },
      { name: "Japan", slug: "japan" },
      { name: "Germany", slug: "germany" },
      { name: "China", slug: "china" },
      { name: "France", slug: "france" },
    ].map((c) => ({
      label: c.name,
      target: null,
      href: `/country/${c.slug}`,
    })),
  };

  const sections = sectionsOverride || defaultSections;
  const geoColumn = geoOverride || defaultGeo;

  const defaultCategories = {
    h: "Categories",
    items: [
      { name: "Politics", slug: "politics" },
      { name: "Business & Economy", slug: "business-economy" },
      { name: "Technology", slug: "technology" },
      { name: "Sports", slug: "sports" },
      { name: "Health & Lifestyle", slug: "health-lifestyle" },
      { name: "Entertainment", slug: "entertainment" },
      { name: "Science", slug: "science" },
      { name: "Climate & Energy", slug: "climate-energy" },
    ].map((c) => ({
      label: c.name,
      target: null,
      href: `/category/${c.slug}`,
    })),
  };
  const categoriesColumn = categoriesOverride || defaultCategories;

  const firstColHeader =
    variant === "country" && countryName
      ? `${countryName} · sections`
      : variant === "category" && categoryName
      ? `${categoryName} · sections`
      : variant === "topics" && topicName
      ? `${topicName} · sections`
      : variant === "search"
      ? "Search · sections"
      : "Sections";

  const cols = [
    {
      h: firstColHeader,
      items: sections,
    },
    geoColumn,
    categoriesColumn,
    {
      h: "Company",
      items: [
        {
          label: "About",
          action: () => setAboutOpen(true),
          testid: "footer-link-company-about",
          subtitle: "Who we are & why we exist",
        },
        {
          label: "Legal",
          action: () => openLegal("terms"),
          testid: "footer-link-company-legal",
          subtitle: "Terms · Privacy · Cookies",
        },
        {
          label: "Support",
          action: () => setSupportOpen(true),
          testid: "footer-link-company-support",
          subtitle: "Ticket — reply within 24 h",
        },
      ],
      variant: "actions",
    },
  ];

  return (
    <>
      <footer
        className="border-t border-[var(--hairline)] bg-[var(--paper)]"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        data-testid="site-footer"
      >
        <div className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-10 pt-14 pb-10">
          <div className="grid grid-cols-12 gap-4 sm:gap-8 lg:gap-12">
            <div className="col-span-12 lg:col-span-4">
              <img
                src={LOGO_URL}
                alt="Daily News Highlights"
                className="h-12 w-auto"
                data-testid="site-footer-logo"
              />
              <p className="font-serif text-[20px] leading-[1.3] text-[var(--ink)] mt-5 max-w-xs">
                Understand the world with clarity, context, and confidence.
              </p>
              <p className="font-sans text-[13.5px] text-[var(--muted)] mt-4 max-w-sm leading-[1.6]">
                A premium global intelligence platform. Curated daily across 196
                countries, in English, from 304+ sources.
              </p>
              <div className="mt-6 flex items-center gap-2 flex-wrap min-w-0">
                <button
                  className="dnh-chip inline-flex items-center gap-2 h-9 px-3 rounded-full border border-[var(--hairline)] font-sans text-[12.5px] text-[var(--ink-2)]"
                  data-testid="footer-language-switcher"
                >
                  <Globe2 size={14} strokeWidth={1.75} />
                  English (Global)
                  <ChevronDown size={12} strokeWidth={2} />
                </button>
              </div>
              <InstallAppBadge />
            </div>

            <div className="col-span-12 lg:col-span-8 min-w-0 grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-8 [&>*]:min-w-0 [&_a]:break-words">
              {cols.map((col) => (
                <div key={col.h}>
                  <div className="font-mono text-[10.5px] tracking-[0.18em] uppercase text-[var(--muted)]">
                    {col.h}
                  </div>

                  {col.variant === "actions" ? (
                    <ul className="mt-4 space-y-2.5">
                      {col.items.map((it) => (
                        <li key={it.label}>
                          <button
                            type="button"
                            onClick={it.action}
                            data-testid={it.testid}
                            className="group block text-left w-full rounded-[8px] px-2.5 py-2 -mx-2.5 transition-colors hover:bg-[var(--surface-2)]"
                          >
                            <div className="font-sans text-[14px] text-[var(--ink)] group-hover:text-[var(--crimson)] transition-colors font-medium">
                              {it.label}
                            </div>
                            <div className="font-mono text-[10px] tracking-[0.08em] text-[var(--muted)] mt-0.5">
                              {it.subtitle}
                            </div>
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <ul className="mt-4 space-y-3">
                      {col.items.map((it) => {
                        const hasTarget = !!it.target;
                        const isRoute = !!it.href;
                        const anchor = hasTarget
                          ? it.target.match(/"([^"]+)"/)?.[1] || ""
                          : "";
                        const finalHref = isRoute
                          ? it.href
                          : hasTarget
                          ? `#${anchor}`
                          : "#";
                        const testId = `footer-link-${col.h
                          .toLowerCase()
                          .replace(/[^a-z0-9]+/g, "-")}-${it.label
                          .toLowerCase()
                          .replace(/[^a-z0-9]+/g, "-")}`;
                        const linkClassName = "font-sans text-[13.5px] text-[var(--ink-2)] hover:text-[var(--crimson)] transition-colors";
                        return (
                          <li key={it.label}>
                            {isRoute ? (
                              <Link to={finalHref} data-testid={testId} className={linkClassName}>
                                {it.label}
                              </Link>
                            ) : (
                              <a
                                href={finalHref}
                                onClick={hasTarget ? scrollToSelector(it.target) : (e) => e.preventDefault()}
                                data-testid={testId}
                                className={linkClassName}
                              >
                                {it.label}
                              </a>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="mt-12 pt-6 border-t border-[var(--hairline)] flex items-center justify-between gap-4 flex-wrap">
            <div className="font-mono text-[11px] text-[var(--muted)]">
              © {new Date().getFullYear()} Daily News Highlights · All rights
              reserved · Curated, not generated
            </div>
            <div className="font-mono text-[11px] text-[var(--muted)] flex items-center gap-5 flex-wrap">
              <button
                type="button"
                onClick={() => openLegal("terms")}
                className="hover:text-[var(--crimson)] transition-colors"
                data-testid="footer-bottom-terms"
              >
                Terms
              </button>
              <button
                type="button"
                onClick={() => openLegal("privacy")}
                className="hover:text-[var(--crimson)] transition-colors"
                data-testid="footer-bottom-privacy"
              >
                Privacy
              </button>
              <button
                type="button"
                onClick={() => openLegal("cookies")}
                className="hover:text-[var(--crimson)] transition-colors"
                data-testid="footer-bottom-cookies"
              >
                Cookies
              </button>
              <Link
                to="/briefs"
                className="hover:text-[var(--crimson)] transition-colors"
                data-testid="footer-bottom-sitemap"
              >
                Archive
              </Link>
              <Link
                to="/topics"
                className="hover:text-[var(--crimson)] transition-colors"
                data-testid="footer-bottom-rss"
              >
                Topics
              </Link>
            </div>
          </div>
        </div>
      </footer>

      <AboutModal open={aboutOpen} onOpenChange={setAboutOpen} />
      <LegalModal
        open={legalOpen}
        onOpenChange={setLegalOpen}
        initialTab={legalTab}
      />
      <SupportModal open={supportOpen} onOpenChange={setSupportOpen} />
    </>
  );
};
